import React, { useState, useEffect, useRef, useCallback } from 'react';
import { BiHeadphone } from 'react-icons/bi';
import { MdClose, MdMic, MdMicOff, MdSignalWifi4Bar, MdSignalWifiOff, MdVolumeUp, MdVolumeOff, MdPlayArrow } from 'react-icons/md';
import { ImSpinner2 } from 'react-icons/im';
import { FiVolume2 } from 'react-icons/fi';
import { WebSocketManager } from './WebSocketManager';
import { AudioRecorder, AudioPlayer } from './AudioManagers';
import rammasIcon from '../../images/rammas_icon.gif';

const WEBSOCKET_URL = 'wss://dewa-uaen-dev-rammas-appsvc-ma-cabxcdgmargtdyhr.uaenorth-01.azurewebsites.net/stream-realtime-response';
const AUDIO_CONFIG = {
  inputSampleRate: 48000,
  outputSampleRate: 16000,
  channelCount: 1,
  chunkDuration: 100,
  voiceThreshold: 0.01,
};

const HandsFreeMode = ({ isArabic, onClose }) => {
  const [connectionState, setConnectionState] = useState({ isConnected: false, isConnecting: false, error: null });
  const [audioState, setAudioState] = useState({ isRecording: false, isPlaying: false, voiceDetected: false, isMuted: false });
  const [messages, setMessages] = useState([]);
  const [lastBotMessage, setLastBotMessage] = useState(''); // Store only the last bot response
  const [isVisible, setIsVisible] = useState(false); // Control slide-in animation
  
  const wsManagerRef = useRef(null);
  const audioRecorderRef = useRef(null);
  const audioPlayerRef = useRef(null);
  const hasShownAISpeakingRef = useRef(false); // Track if we've shown "AI speaking" for current response
  const sentAudioHistoryRef = useRef([]); // Store last 5 sent audio chunks for debugging

  // Trigger slide-in animation on mount
  useEffect(() => {
    setIsVisible(true);
  }, []);

  const log = useCallback((level, message, data = null) => {
    console.log(`[HandsFree ${level}]`, message, data || '');
  }, []);

  const addMessage = useCallback((type, text) => {
    setMessages(prev => [...prev.slice(-20), { id: Date.now(), type, text, timestamp: new Date() }]);
  }, []);

  // WebSocket message handler
  const handleWebSocketMessage = useCallback((data) => {
    console.log('╔═══════════════════════════════════════════════════════╗');
    console.log('║ [HandsFree] PROCESSING MESSAGE                        ║');
    console.log('╠═══════════════════════════════════════════════════════╣');
    console.log('║ Type:', data.type.padEnd(48), '║');
    console.log('╚═══════════════════════════════════════════════════════╝');
    
    if (data.delta) {
      console.log('  → Delta content:', data.delta);
    }
    if (data.response) {
      console.log('  → Response object:', data.response);
    }
    if (data.item) {
      console.log('  → Item object:', data.item);
    }
    if (data.error) {
      console.log('  → Error:', data.error);
    }
    
    switch (data.type) {
      case 'session.created':
        log('info', 'Session created', data.session);
        addMessage('system', `✓ ${isArabic ? 'تم إنشاء الجلسة' : 'Session created'}`);
        break;
        
      case 'session.updated':
        log('info', 'Session updated', data.session);
        addMessage('system', `✓ ${isArabic ? 'تم تحديث الجلسة' : 'Session updated'}`);
        break;
        
      case 'input_audio_buffer.committed':
        log('info', 'Audio buffer committed');
        addMessage('user', `→ ${isArabic ? 'تم إرسال الصوت' : 'Audio sent'}`);
        break;
        
      case 'input_audio_buffer.speech_started':
        log('info', 'Speech detected by server');
        addMessage('system', `🎤 ${isArabic ? 'تم اكتشاف الكلام' : 'Speech detected'}`);
        break;
        
      case 'input_audio_buffer.speech_stopped':
        log('info', 'Speech ended by server');
        addMessage('system', `⏸️ ${isArabic ? 'انتهى الكلام' : 'Speech ended'}`);
        break;
        
      case 'conversation.item.created':
        log('info', 'Conversation item created', data.item);
        break;
        
      case 'response.created':
        log('info', 'Response started', data.response);
        addMessage('system', `⚡ ${isArabic ? 'جاري معالجة الرد' : 'Processing response'}`);
        break;
        
      case 'response.output_item.added':
        log('info', 'Output item added', data.item);
        break;
        
      case 'response.content_part.added':
        log('info', 'Content part added', data.part);
        break;
        
      case 'response.audio_transcript.delta':
        if (data.delta) {
          log('debug', 'Transcript delta:', data.delta);
        }
        break;
        
      case 'response.audio.delta':
        console.log('╔════════════════════════════════════════════════════════════════╗');
        console.log('║ [HandsFree] 🔊 AUDIO DELTA EVENT RECEIVED                    ║');
        console.log('╠════════════════════════════════════════════════════════════════╣');
        console.log('║ Full data object:', data);
        console.log('║ data.delta:', data.delta);
        console.log('║ data.delta type:', typeof data.delta);
        if (data.delta) {
          console.log('║ data.delta keys:', Object.keys(data.delta));
        }
        console.log('╚════════════════════════════════════════════════════════════════╝');
        
        // The audio might be in data.delta directly (as a string)
        const audioData = data.delta?.audio || data.delta;
        
        if (audioData && typeof audioData === 'string') {
          console.log(`  → 🔊 Audio chunk found: ${audioData.length} characters`);
          
          // ALWAYS play audio regardless of mute state - mute only affects microphone input
          audioPlayerRef.current?.playAudioChunk(audioData);
          
          // Show AI is speaking (only once per response)
          if (!hasShownAISpeakingRef.current) {
            hasShownAISpeakingRef.current = true;
            addMessage('assistant', `🔊 ${isArabic ? 'جاري التشغيل...' : 'AI speaking...'}`);
          }
        } else {
          console.warn('[HandsFree] ⚠️ Audio delta event but no audio data:', data);
          console.warn('[HandsFree] Expected string audio data, got:', typeof audioData, audioData);
        }
        break;
        
      case 'response.audio.done':
        log('info', 'Audio response complete');
        addMessage('system', `← ${isArabic ? 'تم استلام الرد' : 'Response complete'}`);
        break;
        
      case 'response.done':
        log('info', 'Response cycle complete', data.response);
        
        // Reset flag for next response
        hasShownAISpeakingRef.current = false;
        
        // Extract transcript for display
        let transcript = '';
        if (data.response?.output && data.response.output.length > 0) {
          const output = data.response.output[0];
          if (output.content && output.content.length > 0) {
            const audioContent = output.content.find(c => c.type === 'audio');
            if (audioContent?.transcript) {
              transcript = audioContent.transcript;
            }
          }
        }
        
        // Display transcript in message log immediately
        if (transcript) {
          addMessage('assistant', `🤖 ${transcript}`);
          setLastBotMessage(transcript); // Store as last bot message for UI display
        }
        
        // Recording is already active and listening - no need to restart
        // AudioRecorder.sendCompleteAudio() already restarted silence detection
        console.log('╔════════════════════════════════════════════════════════════════╗');
        console.log('║ [HandsFree] ✅ Response complete - ready for next turn        ║');
        console.log('╚════════════════════════════════════════════════════════════════╝');
        
        // Log detailed information to console
        console.log('╔══════════════════════════════════════════════════════════════════════╗');
        console.log('║                    📋 RESPONSE COMPLETE                              ║');
        console.log('╠══════════════════════════════════════════════════════════════════════╣');
        
        if (data.response?.output && data.response.output.length > 0) {
          const output = data.response.output[0];
          
          // Show transcript
          if (output.content && output.content.length > 0) {
            const audioContent = output.content.find(c => c.type === 'audio');
            if (audioContent?.transcript) {
              console.log('║ 💬 TRANSCRIPT:                                                       ║');
              console.log('╠══════════════════════════════════════════════════════════════════════╣');
              const words = audioContent.transcript.split(' ');
              let line = '';
              words.forEach(word => {
                if ((line + word).length > 66) {
                  console.log(`║ ${line.padEnd(68)}║`);
                  line = word + ' ';
                } else {
                  line += word + ' ';
                }
              });
              if (line.trim()) {
                console.log(`║ ${line.trim().padEnd(68)}║`);
              }
              console.log('╠══════════════════════════════════════════════════════════════════════╣');
            }
          }
          
          // Show item details
          console.log(`║ Item ID: ${output.id?.padEnd(53) || 'N/A'}║`);
          console.log(`║ Status: ${output.status?.padEnd(54) || 'N/A'}║`);
          console.log(`║ Role: ${output.role?.padEnd(56) || 'N/A'}║`);
        }
        
        // Show usage statistics
        if (data.response?.usage) {
          const usage = data.response.usage;
          console.log('╠══════════════════════════════════════════════════════════════════════╣');
          console.log('║ 📊 TOKEN USAGE:                                                      ║');
          console.log('╠══════════════════════════════════════════════════════════════════════╣');
          console.log(`║ Total Tokens: ${String(usage.total_tokens).padEnd(52)}║`);
          console.log(`║ Input Tokens: ${String(usage.input_tokens).padEnd(52)}║`);
          console.log(`║   - Text: ${String(usage.input_token_details?.text_tokens || 0).padEnd(56)}║`);
          console.log(`║   - Audio: ${String(usage.input_token_details?.audio_tokens || 0).padEnd(55)}║`);
          console.log(`║ Output Tokens: ${String(usage.output_tokens).padEnd(51)}║`);
          console.log(`║   - Text: ${String(usage.output_token_details?.text_tokens || 0).padEnd(56)}║`);
          console.log(`║   - Audio: ${String(usage.output_token_details?.audio_tokens || 0).padEnd(55)}║`);
        }
        
        // Show response metadata
        if (data.response) {
          console.log('╠══════════════════════════════════════════════════════════════════════╣');
          console.log('║ ⚙️ RESPONSE METADATA:                                                ║');
          console.log('╠══════════════════════════════════════════════════════════════════════╣');
          console.log(`║ Response ID: ${data.response.id?.padEnd(51) || 'N/A'}║`);
          console.log(`║ Conversation ID: ${data.response.conversation_id?.padEnd(47) || 'N/A'}║`);
          console.log(`║ Status: ${data.response.status?.padEnd(54) || 'N/A'}║`);
          console.log(`║ Voice: ${data.response.voice?.padEnd(55) || 'N/A'}║`);
          console.log(`║ Temperature: ${String(data.response.temperature || 'N/A').padEnd(51)}║`);
          console.log(`║ Modalities: ${(data.response.modalities?.join(', ') || 'N/A').padEnd(52)}║`);
        }
        
        console.log('╚══════════════════════════════════════════════════════════════════════╝');
        
        addMessage('system', `✓ ${isArabic ? 'اكتمل الرد' : 'Response finished'}`);
        break;
        
      case 'error':
        log('error', 'Server error', data.error);
        addMessage('error', `❌ ${data.error?.message || 'Server error'}`);
        break;
        
      case 'rate_limits.updated':
        log('debug', 'Rate limits updated', data.rate_limits);
        break;
        
      default:
        log('debug', 'Unhandled message type', { type: data.type, data });
    }
  }, [isArabic, addMessage, log]);

  // Audio callbacks
  const handleAudioChunk = useCallback((base64Audio) => {
    // This is now called ONCE with complete audio when user finishes speaking
    console.log(`[HandsFree] 📦 Received complete audio buffer (${base64Audio.length} chars)`);
    
    // Store in history for debugging (keep last 5)
    sentAudioHistoryRef.current = [
      ...sentAudioHistoryRef.current.slice(-4), // Keep last 4
      {
        timestamp: new Date(),
        base64Audio: base64Audio,
        length: base64Audio.length
      }
    ];
    console.log(`[HandsFree] 💾 Stored in history (${sentAudioHistoryRef.current.length} chunks stored)`);
    
    wsManagerRef.current?.sendAudioChunk(base64Audio);
  }, []);

  const handleVoiceDetection = useCallback((detected) => {
    setAudioState(prev => {
      // Don't process voice detection if microphone is muted (input only)
      // Note: This only affects microphone input processing, NOT audio output or WebSocket
      if (prev.isMuted) {
        return prev;
      }
      
      // If user starts speaking while AI is playing, interrupt immediately
      if (detected && prev.isPlaying && !prev.voiceDetected) {
        console.log('╔═══════════════════════════════════════════════════════╗');
        console.log('║ [HandsFree] ⚡ AUTO INTERRUPTION - User Speaking     ║');
        console.log('║ - Stopping AI audio immediately                     ║');
        console.log('║ - Clearing audio queue                              ║');
        console.log('║ - Cancelling server response                        ║');
        console.log('║ - Listening to user...                              ║');
        console.log('╚═══════════════════════════════════════════════════════╝');
        
        // Stop AI audio and clear queue
        audioPlayerRef.current?.stop();
        
        // Cancel ongoing response from server
        wsManagerRef.current?.cancelResponse();
        
        addMessage('system', `⚡ ${isArabic ? 'تم مقاطعة الرد - جاري الاستماع' : 'Interrupted - listening to you'}`);
      }
      
      if (prev.voiceDetected !== detected) {
        return { ...prev, voiceDetected: detected };
      }
      return prev;
    });
  }, [isArabic, addMessage]);

  const handleSilenceDetected = useCallback(() => {
    log('info', '⚡ Silence detected - processing audio');
    
    // OPTIMIZED: Combine operations for faster processing
    // Audio was already sent by AudioRecorder.sendCompleteAudio()
    // Now commit and request response in rapid succession
    const commitPromise = wsManagerRef.current?.commitAudio();
    const responsePromise = wsManagerRef.current?.requestResponse();
    
    addMessage('user', `⚡ ${isArabic ? 'معالجة سريعة...' : 'Fast processing...'}`);
  }, [isArabic, addMessage, log]);

  const handlePlaybackStateChange = useCallback((isPlaying) => {
    setAudioState(prev => ({ ...prev, isPlaying }));
  }, []);

  const handleConnectionChange = useCallback((newState) => {
    setConnectionState(prev => ({ ...prev, ...newState }));
  }, []);

  // Mic button handler - start continuous hands-free mode (not toggle)
  const toggleRecording = useCallback(async () => {
    if (audioState.isRecording) {
      // Already recording - stop hands-free mode completely
      log('info', '🛑 Stopping hands-free mode');
      audioRecorderRef.current?.stop();
      setAudioState(prev => ({ ...prev, isRecording: false, voiceDetected: false, isMuted: false }));
      addMessage('system', `⏹️ ${isArabic ? 'تم إيقاف الوضع الصوتي' : 'Hands-free mode stopped'}`);
    } else {
      // Start continuous hands-free recording - INTERRUPT AI if it's speaking
      try {
        // If AI is currently playing, interrupt it
        if (audioState.isPlaying) {
          console.log('╔═══════════════════════════════════════════════════════╗');
          console.log('║ [HandsFree] ⚡ MANUAL INTERRUPTION (Mic Button)     ║');
          console.log('║ - Stopping AI audio playback                        ║');
          console.log('║ - Clearing audio queue                              ║');
          console.log('║ - Cancelling server response                        ║');
          console.log('║ - Clearing server audio buffer                      ║');
          console.log('║ - Starting user recording                           ║');
          console.log('╚═══════════════════════════════════════════════════════╝');
          
          // Stop AI audio playback and clear queue
          audioPlayerRef.current?.stop();
          
          // Cancel ongoing response from server and clear buffer
          wsManagerRef.current?.cancelResponse();
          
          addMessage('system', `⚡ ${isArabic ? 'تم مقاطعة الرد' : 'AI interrupted - listening'}`);
        }
        
        log('info', '🎙️ Starting continuous hands-free mode');
        
        if (!audioRecorderRef.current) {
          audioRecorderRef.current = new AudioRecorder({
            onAudioChunk: handleAudioChunk,
            onVoiceDetection: handleVoiceDetection,
            onSilenceDetected: handleSilenceDetected,
          });
        }
        
        await audioRecorderRef.current.start();
        setAudioState(prev => ({ ...prev, isRecording: true }));
        addMessage('user', `🎤 ${isArabic ? 'الوضع الصوتي نشط - تحدث الآن' : 'Hands-free active - speak now'}`);
      } catch (error) {
        log('error', '❌ Failed to start recording', error);
        setConnectionState(prev => ({ 
          ...prev, 
          error: isArabic ? 'تم رفض الوصول للميكروفون' : 'Microphone access denied'
        }));
        addMessage('error', `❌ ${isArabic ? 'خطأ في الوصول للميكروفون' : 'Microphone error'}`);
      }
    }
  }, [audioState.isRecording, audioState.isPlaying, isArabic, addMessage, log, handleAudioChunk, handleVoiceDetection, handleSilenceDetected]);

  const handleReconnect = useCallback(() => {
    log('info', 'Manual reconnection requested');
    addMessage('system', isArabic ? 'إعادة الاتصال...' : 'Reconnecting...');
    wsManagerRef.current?.disconnect();
    setTimeout(() => {
      wsManagerRef.current?.connect();
    }, 500);
  }, [addMessage, isArabic, log]);

  // Toggle microphone mute - ONLY affects microphone input, NEVER affects audio output or WebSocket
  const toggleMute = useCallback(() => {
    setAudioState(prev => {
      const newMutedState = !prev.isMuted;
      
      if (newMutedState) {
        // Muting - ONLY stop microphone recording, preserve everything else
        if (audioRecorderRef.current && prev.isRecording) {
          console.log('╔═══════════════════════════════════════════════════════╗');
          console.log('║ [HandsFree] 🔇 MICROPHONE INPUT MUTED                ║');
          console.log('║ - Stopping microphone recording ONLY                ║');
          console.log('║ - WebSocket connection remains ACTIVE               ║');
          console.log('║ - Audio output/playback remains ACTIVE              ║');
          console.log('║ - Hands-free mode state preserved                   ║');
          console.log('║ - Click unmute to resume microphone                 ║');
          console.log('╚═══════════════════════════════════════════════════════╝');
          
          // ONLY stop microphone recording - preserve all other functionality
          audioRecorderRef.current.stop();
        }
        addMessage('system', `🔇 ${isArabic ? 'تم كتم الميكروفون فقط' : 'Microphone input muted only'}`);
      } else {
        // Unmuting - resume microphone recording while preserving all other state
        if (audioRecorderRef.current && prev.isRecording) {
          console.log('╔═══════════════════════════════════════════════════════╗');
          console.log('║ [HandsFree] 🎤 MICROPHONE INPUT UNMUTED              ║');
          console.log('║ - Resuming microphone recording                     ║');
          console.log('║ - WebSocket connection still ACTIVE                 ║');
          console.log('║ - Audio output/playback still ACTIVE                ║');
          console.log('║ - All other functionality preserved                 ║');
          console.log('║ - Listening for speech...                           ║');
          console.log('╚═══════════════════════════════════════════════════════╝');
          
          // Resume microphone recording without affecting other functionality
          audioRecorderRef.current.start().catch(error => {
            console.error('[HandsFree] Failed to resume recording after unmute:', error);
            addMessage('error', `❌ ${isArabic ? 'خطأ في إعادة تشغيل الميكروفون' : 'Failed to resume microphone'}`);
          });
        }
        addMessage('system', `🎤 ${isArabic ? 'تم إلغاء كتم الميكروفون' : 'Microphone input unmuted'}`);
      }
      
      // IMPORTANT: Only change isMuted state - preserve all other audio states
      // isRecording, isPlaying, voiceDetected are intentionally preserved
      return { ...prev, isMuted: newMutedState };
    });
  }, [isArabic, addMessage]);

  // Auto-start recording function
  const startRecordingAutomatically = useCallback(async () => {
    try {
      log('info', '🎙️ Auto-starting hands-free mode');
      
      if (!audioRecorderRef.current) {
        audioRecorderRef.current = new AudioRecorder({
          onAudioChunk: handleAudioChunk,
          onVoiceDetection: handleVoiceDetection,
          onSilenceDetected: handleSilenceDetected,
        });
      }
      
      await audioRecorderRef.current.start();
      setAudioState(prev => ({ ...prev, isRecording: true }));
      addMessage('user', `🎤 ${isArabic ? 'الوضع الصوتي نشط - تحدث الآن' : 'Hands-free active - speak now'}`);
    } catch (error) {
      log('error', '❌ Failed to auto-start recording', error);
      setConnectionState(prev => ({ 
        ...prev, 
        error: isArabic ? 'تم رفض الوصول للميكروفون' : 'Microphone access denied'
      }));
      addMessage('error', `❌ ${isArabic ? 'خطأ في الوصول للميكروفون' : 'Microphone error'}`);
    }
  }, [isArabic, addMessage, log, handleAudioChunk, handleVoiceDetection, handleSilenceDetected]);

  // Initialize WebSocket and Audio on mount
  useEffect(() => {
    log('info', 'Initializing hands-free mode');

    // Initialize WebSocket Manager
    wsManagerRef.current = new WebSocketManager(WEBSOCKET_URL, {
      onMessage: handleWebSocketMessage,
      onConnectionChange: handleConnectionChange,
    });
    wsManagerRef.current.connect();

    // Initialize Audio Player
    audioPlayerRef.current = new AudioPlayer({
      onPlaybackStateChange: handlePlaybackStateChange,
    });

    // Auto-start recording immediately after initialization
    const timer = setTimeout(() => {
      startRecordingAutomatically();
    }, 500); // Reduced delay for faster start

    // Cleanup on unmount - use cleanup() to fully teardown resources
    return () => {
      clearTimeout(timer);
      log('info', 'Cleaning up resources');
      audioRecorderRef.current?.cleanup();
      audioPlayerRef.current?.cleanup();
      wsManagerRef.current?.disconnect();
    };
  }, [handleWebSocketMessage, handleConnectionChange, handlePlaybackStateChange, log, startRecordingAutomatically]);

  return (
    <div className={`fixed inset-0 z-50 flex flex-col transition-transform duration-700 ease-out ${
      isVisible ? 'translate-y-0' : '-translate-y-full'
    }`}>
      {/* White Translucent Background */}
      <div className="absolute inset-0 backdrop-blur-xl" 
           style={{ 
             background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.85) 0%, rgba(248, 250, 252, 0.90) 50%, rgba(241, 245, 249, 0.95) 100%)'
           }}></div>
      
      {/* Subtle overlay pattern for texture */}
      <div className="absolute inset-0 opacity-5"
      ></div>
      
      {/* Close button - positioned absolutely outside content flow */}
      <button 
        onClick={() => {
          // Trigger slide-up animation first
          setIsVisible(false);
          // Delay the actual close to allow animation to complete
          setTimeout(() => {
            onClose();
          }, 700); // Match the animation duration
        }}
        className="absolute top-6 right-6 z-20 p-3 text-gray-700 hover:text-gray-900 hover:bg-white/80 rounded-full transition-all duration-200 border border-gray-300/50 backdrop-blur-sm shadow-md"
      >
        <MdClose className="text-2xl" />
      </button>

      {/* Content overlay - full height without header blocking */}
      <div className="relative z-10 flex flex-col h-full">
        {/* Main content area with adaptive layout and guaranteed visibility */}
        <div className="flex-1 flex overflow-hidden">
          {/* Side Controls Panel - Only visible when there's transcribed text and not on mobile */}
          {lastBotMessage && (
            <div className="hidden md:flex flex-col items-center justify-center space-y-6 w-24 flex-shrink-0 p-2">
              {/* Mute Button - Side Position */}
              <button
                onClick={toggleMute}
                disabled={!connectionState.isConnected}
                className="relative w-16 h-16 rounded-full flex items-center justify-center transition-all duration-300 border-2 shadow-2xl disabled:opacity-50 disabled:cursor-not-allowed backdrop-blur-sm hover:scale-105"
                style={{
                  backgroundColor: audioState.isMuted ? '#E26D5A' : '#0B7BC1',
                  borderColor: audioState.isMuted ? '#E26D5A' : '#0B7BC1',
                  boxShadow: audioState.isMuted 
                    ? '0 20px 40px -10px rgba(226, 109, 90, 0.5)'
                    : '0 20px 40px -10px rgba(11, 123, 193, 0.5)'
                }}
                title={audioState.isMuted ? (isArabic ? 'إلغاء كتم الميكروفون' : 'Unmute microphone') : (isArabic ? 'كتم الميكروفون' : 'Mute microphone')}
              >
                {audioState.isMuted ? (
                  <MdMicOff className="text-2xl text-white" />
                ) : (
                  <MdMic className="text-2xl text-white" />
                )}
              </button>

              {/* Reconnect Button - Side Position */}
              {!connectionState.isConnected && !connectionState.isConnecting && (
                <button
                  onClick={handleReconnect}
                  className="px-2 py-1 text-white rounded-lg transition-all border-2 backdrop-blur-sm shadow-lg font-bold text-xs hover:scale-105"
                  style={{ 
                    fontFamily: 'Arial, sans-serif',
                    backgroundColor: '#6247AA',
                    borderColor: '#6247AA',
                    boxShadow: '0 15px 25px -5px rgba(98, 71, 170, 0.5)'
                  }}
                >
                  {isArabic ? 'إعادة' : 'Reconnect'}
                </button>
              )}
            </div>
          )}

          {/* Main Content Area with guaranteed scroll */}
          <div className="flex-1 flex flex-col min-h-0">
            {/* Upper section with visualizer and status - added top spacing */}
            <div className="flex-shrink-0 flex flex-col items-center justify-center  pt-24 space-y-6">
              {/* Audio Visualizer - Responsive sizing */}
              <div className="relative">
                <div className={`rounded-full border-4 flex items-center justify-center transition-all duration-500 ${
                  lastBotMessage ? 'w-40 h-40 md:w-40 md:h-40' : 'w-48 h-48 md:w-64 md:h-64'
                } ${
                  audioState.isMuted
                    ? 'border-red-400/70 bg-red-100/50 shadow-2xl shadow-red-500/40'
                    : audioState.isRecording
                    ? audioState.voiceDetected
                      ? 'border-green-500/90 bg-green-100/60 shadow-3xl shadow-green-400/50 scale-110'
                      : 'border-green-400/70 bg-green-50/50 shadow-2xl shadow-green-300/30 scale-105'
                    : audioState.isPlaying
                    ? 'border-blue-500/80 bg-blue-100/50 shadow-2xl shadow-blue-400/40 scale-105'
                    : 'border-gray-300/60 bg-gray-50/30 shadow-xl shadow-gray-400/20'
                }`}>
                  {audioState.isMuted ? (
                    <MdMicOff className={`text-red-500 animate-pulse ${lastBotMessage ? 'text-4xl md:text-5xl' : 'text-6xl md:text-8xl'}`} />
                  ) : audioState.isRecording ? (
                    <img 
                      src={rammasIcon} 
                      alt="RAMMAS" 
                      className={`transition-all duration-300 ${lastBotMessage ? 'w-16 h-16 md:w-20 md:h-20' : 'w-24 h-24 md:w-32 md:h-32'} ${
                        audioState.voiceDetected ? 'animate-pulse scale-110' : 'scale-100'
                      }`} 
                    />
                  ) : audioState.isPlaying ? (
                    <MdPlayArrow className={`text-blue-600 animate-pulse ${lastBotMessage ? 'text-4xl md:text-5xl' : 'text-6xl md:text-8xl'}`} />
                  ) : (
                    <img 
                      src={rammasIcon} 
                      alt="RAMMAS" 
                      className={`opacity-50 ${lastBotMessage ? 'w-16 h-16 md:w-20 md:h-20' : 'w-24 h-24 md:w-32 md:h-32'}`} 
                    />
                  )}
                </div>

                {/* Enhanced animated rings with colorful accents */}
                {/* Show rings for recording only when not muted, but always show for AI playing */}
                {((audioState.isRecording && !audioState.isMuted) || audioState.isPlaying) && (
                  <>
                    <div className="absolute inset-0 rounded-full border-2 animate-ping" 
                         style={{ 
                           borderColor: '#007560',
                           animationDuration: '2s' 
                         }} />
                    <div className={`absolute rounded-full border-2 animate-ping ${lastBotMessage ? 'inset-4' : 'inset-8'}`}
                         style={{ 
                           borderColor: '#0B7BC1',
                           animationDuration: '3s',
                           animationDelay: '0.5s' 
                         }} />
                    <div className={`absolute rounded-full border-2 animate-ping ${lastBotMessage ? 'inset-8' : 'inset-16'}`}
                         style={{ 
                           borderColor: '#E26D5A',
                           animationDuration: '4s',
                           animationDelay: '1s' 
                         }} />
                    {audioState.voiceDetected && !audioState.isMuted && (
                      <div className={`absolute rounded-full border-2 animate-ping ${lastBotMessage ? 'inset-12' : 'inset-20'}`}
                           style={{ 
                             borderColor: '#6247AA',
                             animationDuration: '1s' 
                           }} />
                    )}
                  </>
                )}
                
                {/* Mute indicator rings */}
                {audioState.isMuted && (
                  <>
                    <div className="absolute inset-0 rounded-full border-2 border-red-400/60 animate-ping" 
                         style={{ animationDuration: '1.5s' }} />
                    <div className={`absolute rounded-full border-2 border-red-300/50 animate-ping ${lastBotMessage ? 'inset-8' : 'inset-16'}`}
                         style={{ animationDuration: '2.5s', animationDelay: '0.7s' }} />
                  </>
                )}
              </div>

              {/* Status Text - Responsive sizing */}
              <div className="text-center space-y-2">
                <h2 className={`font-bold tracking-wide ${lastBotMessage ? 'text-xl md:text-2xl' : 'text-3xl md:text-4xl'}`} 
                    style={{ fontFamily: 'Arial, sans-serif', color: '#007560' }}>
                  {audioState.isMuted
                    ? (isArabic ? '🔇 الميكروفون مكتوم' : '🔇 Microphone Muted')
                    : audioState.isRecording
                    ? audioState.voiceDetected
                      ? (isArabic ? '🎤 جاري الاستماع...' : 'Listening...')
                      : (isArabic ? '💬 تحدث الآن' : 'Speak Now')
                    : audioState.isPlaying
                    ? (isArabic ? '🔊 رامَّس يتحدث...' : 'RAMMAS Speaking...')
                    : (isArabic ? '🎙️ اضغط للبدء' : '🎙️ Press to Start Talking')
                  }
                </h2>
                {!lastBotMessage && (
                  <p className="text-green-600 text-lg md:text-xl font-medium" style={{ fontFamily: 'Arial, sans-serif' }}>
                    {audioState.isMuted 
                      ? (isArabic ? 'اضغط لإلغاء الكتم' : 'Click to unmute')
                      : (isArabic ? 'محادثة صوتية ذكية من ديوا' : 'Rammas')
                    }
                  </p>
                )}
              </div>
            </div>

            {/* Transcription Area - Hidden on mobile, scrollable on desktop */}
            {lastBotMessage && (
              <div className="hidden md:flex flex-1 min-h-0 p-6 overflow-y-auto">
                <div className="max-w-4xl mx-auto">
                  <div className="p-6 bg-white rounded-2xl shadow-lg" 
                       style={{ 
                         boxShadow: "0px 3px 8px -1px #3232470D, 1px 1px 5px 1px #D3D3D3"
                       }}>
                    <div className="flex items-start space-x-4">
                      <div className="flex-shrink-0 w-12 h-12 rounded-full flex items-center justify-center bg-gray-100 border border-gray-200 overflow-hidden">
                        <img 
                          src={rammasIcon} 
                          alt="RAMMAS" 
                          className="w-8 h-8 object-contain"
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-semibold mb-2 text-gray-700" style={{ fontFamily: 'Arial, sans-serif' }}>
                          {isArabic ? 'رامَّس' : 'RAMMAS'}
                        </div>
                        <p className="font-medium text-lg leading-relaxed text-gray-800 break-words" 
                           style={{ fontFamily: 'Arial, sans-serif' }}>
                          {lastBotMessage}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Center Controls - Always visible on mobile, hidden on desktop when transcription exists */}
            <div className={`flex-shrink-0 flex items-center justify-center p-6 ${lastBotMessage ? 'md:hidden' : ''}`}>
              {/* Mute Button - Centered */}
              <button
                onClick={toggleMute}
                disabled={!connectionState.isConnected}
                className="relative w-20 h-20 md:w-28 md:h-28 rounded-full flex items-center justify-center transition-all duration-300 border-2 shadow-2xl disabled:opacity-50 disabled:cursor-not-allowed backdrop-blur-sm hover:scale-105"
                style={{
                  backgroundColor: audioState.isMuted ? '#E26D5A' : '#0B7BC1',
                  borderColor: audioState.isMuted ? '#E26D5A' : '#0B7BC1',
                  boxShadow: audioState.isMuted 
                    ? '0 25px 50px -12px rgba(226, 109, 90, 0.6)'
                    : '0 25px 50px -12px rgba(11, 123, 193, 0.6)'
                }}
                title={audioState.isMuted ? (isArabic ? 'إلغاء كتم الميكروفون' : 'Unmute microphone') : (isArabic ? 'كتم الميكروفون' : 'Mute microphone')}
              >
                {audioState.isMuted ? (
                  <MdMicOff className="text-3xl md:text-5xl text-white" />
                ) : (
                  <MdMic className="text-3xl md:text-5xl text-white" />
                )}
              </button>

              {/* Reconnect Button - Centered below when disconnected */}
              {!connectionState.isConnected && !connectionState.isConnecting && (
                <div className="absolute mt-32 md:mt-40">
                  <button
                    onClick={handleReconnect}
                    className="px-4 py-2 md:px-6 md:py-3 text-white rounded-xl md:rounded-2xl transition-all border-2 backdrop-blur-sm shadow-xl font-bold text-sm md:text-base hover:scale-105"
                    style={{ 
                      fontFamily: 'Arial, sans-serif',
                      backgroundColor: '#6247AA',
                      borderColor: '#6247AA',
                      boxShadow: '0 20px 25px -5px rgba(98, 71, 170, 0.5)'
                    }}
                  >
                    {isArabic ? 'إعادة الاتصال' : 'Reconnect'}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>       
      </div>
    </div>
  );
};

export default HandsFreeMode;
